import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ILoadBoardPost } from 'types/loadBoardPosts'
import { IShipment } from 'types/shipment'
import {
  openLoadBoardPostModal,
  getLoadBoardPosts,
  openMarketplaceSettingsModal,
} from 'utils/actions/loadBoardPosts'
import { IStore } from 'apps/booking/utils/reducers'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { getTimeAgoShort } from 'utils/formatDatetime'
import { EditPostDialog } from './EditPostDialog'
import { LoadBoardPosts } from './LoadBoardPosts'
import { MarketplaceSettings } from './MarketplaceSettings'
import { MarketplaceSettingsDialog } from './MarketplaceSettingsDialog'
import { MarketplaceStatus } from './MarketplaceStatus'
import { SimilarPosts } from './SimilarPosts'

interface ISelector {
  shipment?: IShipment
  posts: ILoadBoardPost[]
  similarPosts: ILoadBoardPost[]
  loading: boolean
  error: string
  showModal: boolean
  showSettingsModal: boolean
  activeShipmentId?: number
}

export const Posts = () => {
  const { trackEvent } = useMPAnalytics()
  const dispatch = useDispatch()
  const openModal = () => {
    trackEvent('Booking', 'OPENED_EDIT_DAT_POST_MODAL')
    dispatch(openLoadBoardPostModal())
  }
  const openSettingsModal = () => {
    trackEvent('Booking', 'OPENED_MARKETPLACE_SETTINGS_MODAL')
    dispatch(openMarketplaceSettingsModal())
  }
  const {
    activeShipmentId,
    error,
    loading,
    posts,
    shipment,
    showModal,
    showSettingsModal,
    similarPosts,
  } = useSelector<IStore, ISelector>(({ loadBoardPosts, sidebar: { activeRowId, data } }) => ({
    shipment: data?.shipment,
    posts: loadBoardPosts.posts,
    similarPosts: loadBoardPosts.similarPosts,
    loading: loadBoardPosts.loading,
    error: loadBoardPosts.error,
    showModal: loadBoardPosts.showModal,
    showSettingsModal: loadBoardPosts.showSettingsModal,
    activeShipmentId: activeRowId,
  }))

  useEffect(() => {
    if (activeShipmentId) {
      dispatch(getLoadBoardPosts(activeShipmentId.toString()))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShipmentId])

  const updatedByName =
    posts[0] && posts[0].updatedBy
      ? `${posts[0].updatedBy.firstName} ${posts[0].updatedBy.lastName}`
      : ''
  const updatedAtTime = posts[0] && posts[0].updatedAt ? getTimeAgoShort(posts[0].updatedAt) : ''
  const createdAtTime = posts[0] && posts[0].createdAt ? getTimeAgoShort(posts[0].createdAt) : ''
  let editedString = ''
  if (updatedByName) {
    editedString = `Edited ${updatedAtTime} by ${updatedByName}`
  } else if (createdAtTime) {
    editedString = `Created ${createdAtTime}`
  }

  return (
    <section className='sidebar-body'>
      <div className='ph4 mv2'>
        <MarketplaceStatus reasons={shipment?.info.notMarketableReasons} />
        <MarketplaceSettings
          shipment={shipment}
          openSettings={openSettingsModal}
          editedString={editedString}
        />
        <LoadBoardPosts
          shipment={shipment}
          posts={posts}
          openEditPosts={openModal}
          editedString={editedString}
          loading={loading}
          error={error}
        />
        <SimilarPosts shipment={shipment} posts={similarPosts} />
      </div>
      {showModal && <EditPostDialog posts={posts} />}
      {showSettingsModal && <MarketplaceSettingsDialog />}
    </section>
  )
}
